"use client"

import Link from "next/link"
import { useTheme } from "next-themes"
import { Button } from "@/components/ui/button"
import { Sun, Moon, ArrowUp, Twitter, Linkedin, Facebook, Instagram } from "lucide-react"
import { cn } from "@/lib/utils"
import Image from "next/image"

function handleScrollTop() {
  window.scroll({
    top: 0,
    behavior: "smooth",
  });
}

// Define navigation structure based on current site
const navigation = {
  company: [
    { name: "About", href: "/about" },
    { name: "Contact", href: "/contact" },
    // Add other relevant company links if they exist
  ],
  resources: [
    { name: "Articles", href: "/articles" },
    { name: "Pricing", href: "/pricing" },
    // Add FAQ link if page exists
  ],
  legal: [
    { name: "Privacy Policy", href: "/privacy" }, // Placeholder
    { name: "Terms of Service", href: "/terms" }, // Placeholder
  ],
}

const socialLinks = [
  { name: "Twitter", href: "https://twitter.com/brainpercent", Icon: Twitter },
  { name: "LinkedIn", href: "https://linkedin.com/company/brainpercent", Icon: Linkedin },
  { name: "Facebook", href: "https://facebook.com/brainpercent", Icon: Facebook },
  { name: "Instagram", href: "https://instagram.com/brainpercent", Icon: Instagram },
]

export default function SiteFooter() {
  const { setTheme } = useTheme()

  const socialLinkClasses = "transition-colors hover:text-foreground/80 text-foreground/60 p-2 rounded-md border border-transparent hover:border-border"

  return (
    <footer className="border-t border-border/40 bg-background min-h-16">
      <div className="container mx-auto max-w-7xl px-6 py-12 lg:py-16">
        {/* Top Section: Logo and Description */}
        <div className="flex flex-col items-center md:items-start md:flex-row gap-8 md:gap-12 pb-10 border-b border-border/40">
          <Link href="/" className="flex items-center gap-2 flex-shrink-0">
            <Image 
              src="/images/white_icon_transparent_background (1).webp"
              alt="Brainpercent Logo"
              width={32}
              height={32}
              className="h-8 w-8 object-contain"
            />
            <span className="text-xl font-bold">Brainpercent</span>
          </Link>
          <p className="text-sm text-muted-foreground text-center md:text-left max-w-md">
            Brainpercent has been building software, websites, and AI automations for over 3 years, empowering businesses with cutting-edge technology and services.
          </p>
        </div>

        {/* Contact Info */}
        <div className="mt-8 flex flex-col md:flex-row gap-4 text-sm text-muted-foreground">
          <div>
            <span className="font-medium text-foreground">Phone:</span> <a href="tel:+972528822776" className="hover:text-foreground">+972-52-8822-776</a>
          </div>
          <div>
            <span className="font-medium text-foreground">Email:</span> <a href="mailto:Edward@brainpercent.com" className="hover:text-foreground">Edward@brainpercent.com</a>
          </div>
          <div>
            <span className="font-medium text-foreground">Address:</span> Ha-Nurit 19, Ashdod, Israel
          </div>
        </div>

        {/* Google Reviews Badge */}
        <div className="mt-6 flex items-center gap-2">
          <Link href="https://g.page/r/CaLEj6kF7FMPEBM/review" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm font-medium text-primary hover:underline">
            {Array.from({ length: 5 }).map((_, idx) => (
              <svg key={idx} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-yellow-400">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.966a1 1 0 00.95.69h4.174c.969 0 1.371 1.24.588 1.81l-3.382 2.46a1 1 0 00-.364 1.118l1.287 3.966c.3.921-.755 1.688-1.54 1.118l-3.382-2.46a1 1 0 00-1.176 0l-3.382 2.46c-.784.57-1.838-.197-1.539-1.118l-1.287-3.966a1 1 0 00-.364-1.118L2.05 9.393c-.783-.57-.38-1.81.588-1.81h4.174a1 1 0 00.95-.69l1.286-3.966z" />
              </svg>
            ))}
            <span className="ml-1 text-muted-foreground">15 Google reviews</span>
          </Link>
        </div>

        {/* Middle Section: Navigation Links */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 py-10">
          <div>
            <h3 className="text-sm font-semibold leading-6 text-foreground">Company</h3>
            <ul role="list" className="mt-4 space-y-2">
              {navigation.company.map((item) => (
                <li key={item.name}>
                  <Link href={item.href} className="text-sm leading-6 text-muted-foreground hover:text-foreground">
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-semibold leading-6 text-foreground">Resources</h3>
            <ul role="list" className="mt-4 space-y-2">
              {navigation.resources.map((item) => (
                <li key={item.name}>
                  <Link href={item.href} className="text-sm leading-6 text-muted-foreground hover:text-foreground">
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-semibold leading-6 text-foreground">Legal</h3>
            <ul role="list" className="mt-4 space-y-2">
              {navigation.legal.map((item) => (
                <li key={item.name}>
                  <Link href={item.href} className="text-sm leading-6 text-muted-foreground hover:text-foreground">
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          {/* Optional: Add another column if needed */}
        </div>

        {/* Bottom Section: Social, Theme Toggle, Copyright */}
        <div className="mt-10 border-t border-border/40 pt-8 flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Social Links */}
          <div className="flex space-x-4 md:order-2">
            {socialLinks.map((item) => (
              <Link key={item.name} href={item.href} className={socialLinkClasses} target="_blank" rel="noopener noreferrer">
                <span className="sr-only">{item.name}</span>
                <item.Icon className="h-5 w-5" aria-hidden="true" />
              </Link>
            ))}
          </div>

          {/* Theme Toggle & Scroll Top */}
          <div className="flex items-center justify-center md:order-3">
            <div className="flex items-center rounded-full border border-border/40 p-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setTheme("light")}
                className="rounded-full h-8 w-8"
                aria-label="Switch to light mode"
              >
                <Sun className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleScrollTop}
                className="rounded-full h-8 w-8 mx-1"
                aria-label="Scroll to top"
              >
                <ArrowUp className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setTheme("dark")}
                className="rounded-full h-8 w-8"
                aria-label="Switch to dark mode"
              >
                <Moon className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Copyright */}
          <p className="text-xs leading-5 text-muted-foreground md:order-1">
            &copy; {new Date().getFullYear()} Brainpercent.com. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  )
}

